/**
 * Shared builder: PL `CategoriesShowcaseWidgetData` → domain `CustomSection`.
 *
 * Mirrors `banner-section-builders.ts`: dependency-light, no JSX, so the CATEGORIES_SHOWCASE widget
 * can render the existing `CategoriesShowcaseV2` section component (DS + legacy routing + analytics)
 * without duplicating the proto→domain conversion. The section renders `genericContents` as category
 * cards, so proto `thumbnails[]` are mapped into `GenericContent` (image + link + title).
 */

import type { ContentfulImageForMenuItem } from '@src/quince-site-common/src/domains/contentful/ContentfulImage'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import GenericContent from '@src/quince-site-common/src/domains/GenericContent'
import Image from '@src/quince-site-common/src/domains/Image'
import Link from '@src/quince-site-common/src/domains/Link'

import { makeSection } from './banner-section-builders'
import { buildWidgetTrackingMetaData, buildWidgetTrackingTypes, toRichText } from './widget-utils'

import type {
  CategoriesShowcaseWidgetData,
  CategoryThumbnail,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * thumbnail → GenericContent: only the fields the category card renders (images[0], link, title, id).
 * `titleText` is derived from the plain-string `title` since the proto carries no rich-text field —
 * the legacy (non-DS) card renders `titleText`, not `title`, so without it the label below the card is blank.
 */
function thumbnailToGenericContent(thumbnail: CategoryThumbnail): GenericContent {
  const images = thumbnail.image ? [Image.fromContentful(thumbnail.image as unknown as ContentfulImageForMenuItem)] : []
  return new GenericContent({
    id: thumbnail.id,
    title: thumbnail.title,
    titleText: toRichText(thumbnail.title),
    images,
    colors: [],
    products: [],
    link: new Link({ id: thumbnail.id, navigationPath: thumbnail.navigationPath, scrollToHash: '' }),
  })
}

/**
 * `tracker` mirrors legacy's `SectionRenderer`-injected `home_page` tracker: `CategoriesShowcaseV2`
 * gates the batched category-card impression stream and the tile `product_category_click`
 * `BUTTON_CLICK` on `sectionConfig.tracker` being truthy, so it must land on the built section here.
 *
 * `sectionPosition` is the derived 1-based page position (`useWidgetSectionPosition`) — legacy
 * bakes it into the section payload as both `elementPosition` and
 * `trackingMetaData.context.contentContainerPosition`; the PL wire carries neither.
 */
export function buildCategoriesShowcaseConfig(
  data: CategoriesShowcaseWidgetData,
  tracker?: string,
  sectionPosition?: number,
): CustomSection {
  return makeSection({
    id: data.id,
    displayTitle: data.displayTitle,
    sectionType: data.sectionType as CUSTOM_SECTION_TYPE,
    // Top-level trackingId (read as `element_id` by `getTrackingContext`) — legacy sets this from the
    // BFF section's own `trackingId`; the PL builder only ever nested it inside `trackingMetaData`.
    trackingId: data.analytics?.trackingId,
    tracker,
    elementPosition: sectionPosition,
    // Empty navigationPath ⇒ tile skipped (per proto).
    genericContents: data.thumbnails.filter(thumbnail => thumbnail.navigationPath).map(thumbnailToGenericContent),
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics, sectionPosition),
    // Arms the PL section-level IS_VISIBLE/CLICK emitter (PLSectionTrackingWrapper) once the wire
    // ships trackingTypes — legacy sends ['VISIBLE'] for every home section (proto 3.20.0).
    trackingTypes: buildWidgetTrackingTypes(data.analytics),
    // Contentful section classes (commonClassNames / mobileClassNames / baseDynamicValueClasses /
    // dynamicValueClassesMobile) — applied by the legacy fork's useDynamicStyle; the DS fork
    // ignores them, same as non-PL.
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    mobileDynamicClassesOverrides: data.mobileDynamicClassesOverrides,
  })
}
